import { TILES } from './tiles.js';
import {
	DEFAULT_HEIGHT,
	DEFAULT_WIDTH,
	HEIGHT_RANGE,
	WIDTH_RANGE,
	clampToRange,
	emptyGrid,
	gridWidth,
	resizedGrid,
} from './grid.js';
import { MAX_HEIGHT, MIN_HEIGHT, clampHeight, hasContour, normaliseHeights, slopeGrade } from './contour.js';
import {
	DEFAULT_STEP,
	climbMetres,
	elevationRange,
	hasElevation,
	normaliseElevation,
	stepAt,
	terrainSlopeAt,
} from './elevation.js';
import { hasPinAt, normalisePins, togglePin } from './pins.js';
import {
	LATEST_FORMAT,
	courseFormatOf,
	courseWarnings,
	exportCourse,
	importCourse,
	isV2 as courseIsV2,
} from './course.js';

let courseData = {
	id: 'new_course',
	name: 'New Course',
	description: '',
	formatVersion: 2,
	levels: [],
};
let currentLevelIndex = -1;
let selectedTileType = 'ROUGH';
let isPainting = false;

/**
 * What a click paints: `tiles` the terrain type, `terrain` the whole-hole ground elevation (the green
 * included — there is no separate contour for it any more, see elevation.js). Only `terrain` is
 * meaningful on a v2 course; `tiles` always is.
 */
let editMode = 'tiles';
let selectedHeight = 5;

/**
 * Which gesture painting performs: `brush` paints the cell under the pointer, `select` drags out a
 * rectangle for `fillGrid` to act on, `bucket` floods every connected matching cell, `hill` (terrain
 * mode only) sculpts a smooth rise or dip around a dragged-out radius, and `pin` (v2 only) toggles a cell
 * as one of the cups this hole may be cut to.
 *
 * `pin` is the one tool that writes neither matrix — a pin is a coordinate beside the grid, not a tile in
 * it, for the reason spelled out in pins.js: the hole keeps exactly one `0`, and it moves.
 */
let currentTool = 'brush';

/** The rectangle `select` has committed, in grid coordinates inclusive on both ends. `null` when none. */
let selection = null;

/** Where a `select`/`hill` drag started and where the pointer currently is, while the button is still down. */
let dragAnchor = null;
let dragCurrent = null;

/** Whether the current mode paints the terrain height matrix. */
function isHeightMode() {
	return editMode === 'terrain' && isV2();
}

/** The format the file will be written in. v1 carries neither wind nor green contours. */
function courseFormat() {
	return courseFormatOf(courseData);
}

function isV2() {
	return courseIsV2(courseData);
}

// --- Initialization ---

/** Class on `.editor-layout`, and the key its state is remembered under. */
const PROPERTIES_COLLAPSED = 'properties-collapsed';
const PROPERTIES_ANIMATED = 'properties-animated';
const PROPERTIES_STORAGE_KEY = 'pocketGolfEditorPropertiesCollapsed';

function init() {
	initTileSelector();
	initHeightSelector();
	setupDragAndDrop();

	// Attempt to load from localStorage
	const saved = localStorage.getItem('pocketGolfCourse');
	if (saved) {
		try {
			courseData = JSON.parse(saved);
			currentLevelIndex = courseData.levels.length > 0 ? 0 : -1;
			showSaveStatus('Loaded from LocalStorage');
		} catch (e) {
			console.error('Local storage load failed', e);
			createDefaultCourse();
		}
	} else {
		createDefaultCourse();
	}

	renderAll();

	document.addEventListener('mouseup', () => {
		if (!isPainting) return;
		isPainting = false;

		// Brush painting already saved on every cell (see paintCell); select and hill only act now, once
		// the gesture is finished, so the rectangle or the radius is the one the author actually dragged.
		if (currentTool === 'select' && dragAnchor && dragCurrent) {
			selection = normaliseRect(dragAnchor, dragCurrent);
			dragAnchor = null;
			dragCurrent = null;
			renderGrid();
		} else if (currentTool === 'hill' && dragAnchor && dragCurrent) {
			commitHillDig(dragAnchor, dragCurrent);
			dragAnchor = null;
			dragCurrent = null;
		}

		saveState();
	});

	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape' && selection) {
			clearSelection();
		}
	});
}

function createDefaultCourse() {
	courseData = {
		id: generateUniqueId(),
		name: 'New Course',
		description: '',
		// A new course is authored in the current format. v1 exists so the courses already in the world
		// keep playing as they always have — it is not a thing anyone should have to opt *out* of when
		// starting from scratch, and leaving this unset made every new course silently v1.
		formatVersion: LATEST_FORMAT,
		levels: [],
	};
	// Create 9 holes by default
	for (let i = 1; i <= 9; i++) {
		courseData.levels.push({
			name: `Hole ${i}`,
			par: 3,
			id: computeLevelId(courseData.id, i),
			grid: emptyGrid(),
		});
	}
	currentLevelIndex = 0;
}

function computeLevelId(courseId, indexStr) {
	let base = courseId || 'course';
	let num = String(indexStr).padStart(2, '0');
	return `${base}-${num}`;
}

function generateUniqueId() {
	const timestamp = Date.now().toString(36); // ~8 chars
	const randomPart = Math.random().toString(36).substring(2, 10); // 8 chars
	return (timestamp + randomPart).substring(0, 16);
}

// --- State Management & Saving ---

function saveState() {
	updateCourseDataOutput();
	localStorage.setItem('pocketGolfCourse', JSON.stringify(courseData));
	showSaveStatus('Saved automatically');
}

function showSaveStatus(msg) {
	const status = document.getElementById('saveStatus');
	status.textContent = msg;
	status.style.color = '#238b21';
	setTimeout(() => {
		status.textContent = '';
	}, 3000);
}

// --- Rendering ---

function renderAll() {
	updateAllLevelIds(); // Ensure IDs are in sync
	// A v1 course has no terrain to paint, so a terrain mode left over from a v2 course would leave the
	// grid showing a matrix the file cannot carry.
	if (!isV2()) editMode = 'tiles';

	renderCourseInfo();
	renderTabs();
	renderLevelForm();
	renderPaletteMode();
	renderGrid();
	updateInfo();
	renderValidation();
	updateCourseDataOutput();
}

function updateAllLevelIds() {
	courseData.levels.forEach((level, idx) => {
		level.id = computeLevelId(courseData.id, idx + 1);
	});
}

function renderCourseInfo() {
	document.getElementById('courseId').value = courseData.id || '';
	document.getElementById('courseName').value = courseData.name || '';
	document.getElementById('courseDesc').value = courseData.description || '';

	document.getElementById('courseFormat').value = String(courseFormat());
	// v2-only controls are dimmed rather than removed, so it stays obvious that the feature exists and
	// that the format switch is what unlocks it.
	document.querySelectorAll('.v2-only').forEach((group) => {
		group.dataset.disabled = isV2() ? 'false' : 'true';
	});

	const wind = courseData.wind || {};
	document.getElementById('windSpeedMin').value = wind.speedMin ?? '';
	document.getElementById('windSpeedMax').value = wind.speedMax ?? '';
	document.getElementById('windAngleMin').value = wind.angleMin ?? '';
	document.getElementById('windAngleMax').value = wind.angleMax ?? '';

	// Achievement ID and Leaderboard ID are assigned outside the editor and are not editable here. A
	// course that already carries one keeps it (exportCourse round-trips whatever is on courseData), shown
	// read-only so nobody mistakes it for something the editor manages.
	const achieveInput = document.getElementById('courseAchieve');
	const achieveGroup = document.getElementById('courseAchieveGroup');
	achieveInput.value = courseData.finishAchievementId || '';
	achieveGroup.style.display = courseData.finishAchievementId ? 'flex' : 'none';

	const leaderInput = document.getElementById('courseLeader');
	const leaderGroup = document.getElementById('courseLeaderGroup');
	leaderInput.value = courseData.leaderboardId || '';
	leaderGroup.style.display = courseData.leaderboardId ? 'flex' : 'none';
}

function renderLevelInfo() {
	if (courseData.levels.length === 0) return;
	const level = courseData.levels[currentLevelIndex];

	document.getElementById('levelId').value = level.id || '';
	document.getElementById('levelName').value = level.name || '';
	document.getElementById('levelPar').value = level.par || 3;

	const clubSelect = document.getElementById('levelClub');
	clubSelect.value = level.club || '';

	const angleInput = document.getElementById('levelAngle');
	angleInput.value = level.angle !== undefined ? level.angle : '';
}

function renderTabs() {
	const container = document.getElementById('holeTabs');
	container.innerHTML = '';

	courseData.levels.forEach((level, index) => {
		const tab = document.createElement('div');
		tab.className = `tab ${index === currentLevelIndex ? 'active' : ''}`;
		tab.textContent = `Hole ${index + 1}`;
		tab.onclick = () => switchLevel(index);
		container.appendChild(tab);
	});
}

function renderLevelForm() {
	const isLevelSelected = currentLevelIndex >= 0 && currentLevelIndex < courseData.levels.length;

	// Disable inputs if no level
	const inputs = ['levelName', 'levelPar', 'levelClub', 'levelAngle'];
	inputs.forEach((id) => {
		document.getElementById(id).disabled = !isLevelSelected;
	});

	if (isLevelSelected) {
		const level = courseData.levels[currentLevelIndex];
		document.getElementById('levelId').value = level.id || computeLevelId(courseData.id, currentLevelIndex + 1);
		document.getElementById('levelName').value = level.name || '';
		document.getElementById('levelPar').value = level.par || 3;
		document.getElementById('levelClub').value = level.club || '';
		document.getElementById('levelAngle').value = level.angle !== undefined ? level.angle : '';
	} else {
		document.getElementById('levelId').value = '';
		document.getElementById('levelName').value = '';
		document.getElementById('levelPar').value = '';
		document.getElementById('levelClub').value = '';
		document.getElementById('levelAngle').value = '';
	}
}

function renderGrid() {
	const container = document.getElementById('gridContainer');
	container.innerHTML = '';

	if (currentLevelIndex < 0 || !courseData.levels[currentLevelIndex]) {
		container.innerHTML = '<p style="padding: 20px; color: #999;">No level selected. Click "+ Add Hole" to start.</p>';
		return;
	}

	const level = courseData.levels[currentLevelIndex];
	let grid = level.grid || emptyGrid();

	// Ensure grid is strings map representation
	if (grid.length > 0 && typeof grid[0] === 'string') {
		// String array grid is correct
	} else {
		grid = emptyGrid();
		level.grid = grid;
	}

	// Pruned rather than read raw, so the flags drawn on the grid are exactly the ones the file will
	// carry: painting the cup onto a cell that was an alternate makes it the cup, not both.
	const pins = isV2() ? levelPins(level) : [];

	const paintingTerrain = editMode === 'terrain' && isV2();
	// Only shaped when it is being shown, so a v1 hole never grows a height matrix just by being looked
	// at — a course only carries one once somebody paints one.
	const elevation = paintingTerrain ? levelElevation(level) : null;
	const elevationSpan = paintingTerrain ? elevationRange(elevation) : null;

	// A live marquee while `select` is being dragged, distinct from the rectangle already committed —
	// the two can differ for as long as the mouse is down.
	const marquee = currentTool === 'select' && dragAnchor && dragCurrent ? normaliseRect(dragAnchor, dragCurrent) : null;
	// The `hill` tool previews its own radius the same way, but as a circle rather than a rectangle, and
	// with a per-cell strength so the falloff that will be applied is visible before it is committed.
	const hillPreview =
		currentTool === 'hill' && dragAnchor && dragCurrent
			? { anchor: dragAnchor, radius: Math.max(1, Math.hypot(dragCurrent.x - dragAnchor.x, dragCurrent.y - dragAnchor.y)) }
			: null;

	const columns = gridWidth(grid);

	grid.forEach((rowString, y) => {
		const rowDiv = document.createElement('div');
		rowDiv.className = 'grid-row';

		for (let x = 0; x < columns; x++) {
			const char = rowString.charAt(x) || 'R';
			const tileType = Object.keys(TILES).find((key) => TILES[key].mapChar === char) || 'ROUGH';
			const tile = TILES[tileType];

			const cell = document.createElement('div');
			cell.className = 'grid-cell';
			cell.dataset.x = x;
			cell.dataset.y = y;
			cell.style.backgroundColor = tile.color;

			if (paintingTerrain) {
				decorateTerrainCell(cell, elevation, elevationSpan, x, y);
			} else {
				decorateTileCell(cell, tile);
			}

			// Drawn in both modes: an author sculpting a green needs to see which cells the flag can end
			// up on, since that is most of what the sculpting is for.
			if (isV2() && hasPinAt(pins, x, y)) {
				cell.classList.add('cell-pin');
				cell.textContent = '⛳';
				cell.title = `Alternate pin (${x}, ${y})`;
			}

			if (selection && x >= selection.x0 && x <= selection.x1 && y >= selection.y0 && y <= selection.y1) {
				cell.classList.add('cell-selected');
			}
			if (marquee && x >= marquee.x0 && x <= marquee.x1 && y >= marquee.y0 && y <= marquee.y1) {
				cell.classList.add('cell-marquee');
			}
			if (hillPreview) {
				const dist = Math.hypot(x - hillPreview.anchor.x, y - hillPreview.anchor.y);
				if (dist <= hillPreview.radius) {
					const falloff = hillFalloff(dist, hillPreview.radius);
					cell.classList.add('cell-hill-preview');
					cell.style.setProperty('--hill-opacity', falloff.toFixed(2));
				}
			}

			cell.onmousedown = (e) => {
				e.preventDefault();
				handleCellDown(x, y);
			};

			cell.onmouseenter = (e) => {
				if (e.buttons === 1) {
					handleCellDrag(x, y);
				}
			};

			rowDiv.appendChild(cell);
		}
		container.appendChild(rowDiv);
	});

	updateInfo();
	renderTerrainSummary();
	updateSelectionControls();
}

function decorateTileCell(cell, tile) {
	if (tile.editorIcon) {
		cell.textContent = tile.editorIcon;
	} else if (tile.mapChar === '0') {
		cell.textContent = '⛳';
	} else if (
		tile.mapChar !== 'R' &&
		tile.mapChar !== 'F' &&
		tile.mapChar !== 'G' &&
		tile.mapChar !== 'S' &&
		tile.mapChar !== 'W' &&
		tile.mapChar !== 'O'
	) {
		// For visual clarity if no icon
		cell.textContent = tile.mapChar;
	}
}

/**
 * A cell in terrain mode: shaded by height, labelled with it, and carrying the downhill arrow.
 *
 * Every cell has a height here, unlike the green contour this replaced — there is no inert ground,
 * because all of it is ground, and the green simply takes its shape from whatever it sits on.
 */
function decorateTerrainCell(cell, elevation, range, x, y) {
	const height = stepAt(elevation, x, y);
	cell.classList.add('height-cell');

	// -1 at the lowest point on the hole, +1 at the highest. A hole with no variation yet shades flat,
	// which is honest: there is nothing to see until something differs.
	const span = range ? range.high - range.low : 0;
	const relief = span > 0 ? ((height - range.low) / span) * 2 - 1 : 0;
	const overlay =
		relief >= 0
			? `rgba(255, 255, 255, ${(relief * 0.5).toFixed(3)})`
			: `rgba(0, 0, 0, ${(-relief * 0.45).toFixed(3)})`;
	cell.style.backgroundImage = `linear-gradient(${overlay}, ${overlay})`;

	const label = document.createElement('span');
	label.textContent = String(height);
	cell.appendChild(label);

	const slope = terrainSlopeAt(elevation, x, y);
	if (slope.steepness >= 0.3) {
		const arrow = document.createElement('span');
		arrow.className = 'height-arrow';
		// The glyph points north and the angle is clockwise from north, which is the rotation directly.
		arrow.textContent = '↑';
		arrow.style.transform = `rotate(${slope.angle.toFixed(1)}deg)`;
		arrow.title = `${slopeGrade(slope.steepness)} — falls towards ${Math.round(slope.angle)}°`;
		cell.appendChild(arrow);
	}
}

/**
 * The hole's terrain matrix, created and shaped to the grid on first use.
 *
 * A new one starts at the middle of the range rather than at zero, so an author's first stroke can go
 * either up or down from the ground they are standing on. Starting at 0 would mean every hole could only
 * ever be sculpted upwards.
 */
function levelElevation(level) {
	level.elevation = normaliseElevation(level.elevation, level.grid, DEFAULT_STEP);
	return level.elevation;
}

function renderTerrainSummary() {
	const summary = document.getElementById('terrainSummary');
	if (!summary) return;
	if (!isHeightMode() || currentLevelIndex < 0) {
		summary.textContent = '';
		return;
	}

	const level = courseData.levels[currentLevelIndex];
	const range = elevationRange(levelElevation(level));
	if (!range || range.low === range.high) {
		summary.textContent = `Hole is flat at ${range ? range.low : DEFAULT_STEP}. A flat hole carries no terrain in the file.`;
		return;
	}
	// The number an author is actually designing against, in the units the game's CLIMB readout uses.
	const drop = range.high - range.low;
	const extent = `Ground runs ${range.low}–${range.high} — ${drop} step(s), ${drop * 2} m.`;

	const tee = teeCell(level);
	const cup = cupCell(level);
	if (!tee || !cup) {
		// Without both there is nothing to measure a climb between, and reporting one anyway would
		// mean quietly measuring from the top-left corner.
		summary.textContent = `${extent} Paint a tee and a cup to see how the hole plays.`;
		return;
	}

	const climb = climbMetres(level.elevation, tee, cup);
	const played =
		climb === 0
			? 'The tee and the cup are level.'
			: `The hole plays ${Math.abs(climb)} m ${climb > 0 ? 'uphill' : 'downhill'}.`;
	summary.textContent = `${extent} ${played}`;
}

/** Where the tee is, read from the grid the way the game reads it: the first `X`. `null` if unpainted. */
function teeCell(level) {
	return findChar(level.grid || [], 'X');
}

/** Where the cup is: the first `0`, likewise. */
function cupCell(level) {
	return findChar(level.grid || [], '0');
}

function findChar(grid, char) {
	for (let y = 0; y < grid.length; y++) {
		const x = grid[y].indexOf(char);
		if (x >= 0) return { x, y };
	}
	return null;
}

function updateInfo() {
	if (currentLevelIndex >= 0 && courseData.levels[currentLevelIndex] && courseData.levels[currentLevelIndex].grid) {
		const grid = courseData.levels[currentLevelIndex].grid;
		document.getElementById('levelSize').textContent = `${gridWidth(grid)} x ${grid.length}`;
	} else {
		document.getElementById('levelSize').textContent = `${DEFAULT_WIDTH} x 0`;
	}

	// Counted the way the game counts: the painted cup plus its alternates. "1" is a hole that plays the
	// same every round, which is what every hole was before this existed.
	const pins = document.getElementById('levelPins');
	if (!pins) return;
	if (currentLevelIndex < 0 || !isV2()) {
		pins.textContent = isV2() ? '1' : '1 (v2 only)';
		return;
	}
	const count = 1 + levelPins(courseData.levels[currentLevelIndex]).length;
	pins.textContent = count === 1 ? '1 — cut the same way every round' : `${count} — one is drawn each round`;
}

function updateCourseDataOutput() {
	// We don't need this to be visible to the user anymore, but keeping it updated in case we want to debug
	const jsonOutput = JSON.stringify(courseData, null, 4);
	document.getElementById('courseDataOutput').value = jsonOutput;
}

// --- UI Interaction Handlers ---

function initTileSelector() {
	const selector = document.getElementById('tileSelector');
	selector.innerHTML = '';

	Object.keys(TILES).forEach((tileType) => {
		const tile = TILES[tileType];
		const button = document.createElement('div');
		button.className = 'tile-button';
		if (tileType === selectedTileType) {
			button.classList.add('selected');
		}
		button.style.backgroundColor = tile.color;

		if (tile.editorIcon) {
			button.textContent = tile.editorIcon;
		} else {
			button.textContent = tile.mapChar;
			button.style.fontSize = '20px';
			button.style.fontWeight = 'bold';
			button.style.color = tile.color === '#555555' ? '#fff' : '#333';
		}

		const label = document.createElement('div');
		label.className = 'label';
		label.textContent = tile.mapTitle;
		button.appendChild(label);

		button.onclick = () => selectTile(tileType);
		selector.appendChild(button);
	});
}

function selectTile(tileType) {
	selectedTileType = tileType;
	document.getElementById('selectedTile').textContent = TILES[tileType].mapTitle;
	initTileSelector(); // Re-render to show selected border
}

function updateCourseData() {
	courseData.id = document.getElementById('courseId').value;
	courseData.name = document.getElementById('courseName').value;
	courseData.description = document.getElementById('courseDesc').value;

	// Wind. Dropped entirely when there is no speed to blow, so a calm course carries no wind entry
	// rather than a zeroed one — the game treats both as calm, and a file should not claim a feature it
	// does not use.
	const windSpeedMin = parseFloat(document.getElementById('windSpeedMin').value);
	const windSpeedMax = parseFloat(document.getElementById('windSpeedMax').value);
	const windAngleMin = parseFloat(document.getElementById('windAngleMin').value);
	const windAngleMax = parseFloat(document.getElementById('windAngleMax').value);
	const topSpeed = Number.isFinite(windSpeedMax) ? windSpeedMax : windSpeedMin;

	// Only on a v2 course. The format switch owns the version now, rather than wind quietly bumping it:
	// with sloped greens in the format too, "which features may I use" is a decision the author should
	// make once and see, not something that happens as a side effect of typing in a box.
	if (isV2() && Number.isFinite(topSpeed) && topSpeed > 0) {
		courseData.wind = {
			speedMin: Number.isFinite(windSpeedMin) ? windSpeedMin : 0,
			speedMax: topSpeed,
			angleMin: Number.isFinite(windAngleMin) ? windAngleMin : 0,
			angleMax: Number.isFinite(windAngleMax) ? windAngleMax : 359,
		};
	} else {
		delete courseData.wind;
	}

	updateAllLevelIds(); // Update level IDs immediately when Course ID changes
	renderLevelForm(); // Re-render to show updated level ID
	saveState();
}

function updateLevelData() {
	if (currentLevelIndex < 0) return;
	const level = courseData.levels[currentLevelIndex];

	level.name = document.getElementById('levelName').value;

	const parVal = parseInt(document.getElementById('levelPar').value);
	if (!isNaN(parVal)) level.par = parVal;

	const club = document.getElementById('levelClub').value;
	if (club) level.club = club;
	else delete level.club;

	const angle = document.getElementById('levelAngle').value;
	if (angle && !isNaN(parseInt(angle))) {
		level.angle = parseInt(angle);
	} else {
		delete level.angle;
	}

	saveState();
}

function switchLevel(index) {
	if (index >= 0 && index < courseData.levels.length) {
		currentLevelIndex = index;
	} else {
		currentLevelIndex = -1;
	}
	// A selection is a pair of grid coordinates; nothing says they still make sense against a different
	// hole's grid, which can be a different height.
	clearSelection();
	renderTabs();
	renderLevelForm();
	renderGrid();
	updateInfo();
}

function addLevel() {
	const numLevels = courseData.levels.length;
	const newLevel = {
		name: `Hole ${numLevels + 1}`,
		par: 3,
		grid: emptyGrid(),
	};
	courseData.levels.push(newLevel);
	currentLevelIndex = courseData.levels.length - 1;
	clearSelection();
	updateAllLevelIds();
	renderAll();
	saveState();
}

function deleteLevel() {
	if (currentLevelIndex < 0) return;
	if (confirm('Are you sure you want to delete this hole?')) {
		courseData.levels.splice(currentLevelIndex, 1);
		currentLevelIndex = courseData.levels.length > 0 ? Math.min(currentLevelIndex, courseData.levels.length - 1) : -1;
		clearSelection();
		updateAllLevelIds();
		renderAll();
		saveState();
	}
}

// --- Painting tools ---
//
// `currentTool` picks the gesture; every tool still writes through the same two matrices `paintCell`
// always did (the tile grid, or the terrain elevation in v2's terrain mode). A mousedown on a cell goes
// through `handleCellDown`, a drag while the button is held through `handleCellDrag`, and `select`/`hill`
// both defer their actual write to mouseup (see `init`) so the rectangle or radius used is the one the
// author finished dragging, not whatever it was mid-gesture.

function handleCellDown(x, y) {
	if (currentLevelIndex < 0) return;
	isPainting = true;

	if (currentTool === 'pin') {
		togglePinAt(x, y);
		isPainting = false;
	} else if (currentTool === 'bucket') {
		floodFill(x, y);
		isPainting = false;
	} else if (currentTool === 'select') {
		dragAnchor = { x, y };
		dragCurrent = { x, y };
		selection = null;
		renderGrid();
	} else if (currentTool === 'hill') {
		if (!(editMode === 'terrain' && isV2())) return;
		dragAnchor = { x, y };
		dragCurrent = { x, y };
		renderGrid();
	} else {
		paintCell(x, y);
	}
}

function handleCellDrag(x, y) {
	if (!isPainting) return;

	if (currentTool === 'brush') {
		paintCell(x, y);
	} else if (currentTool === 'select' || currentTool === 'hill') {
		dragCurrent = { x, y };
		renderGrid();
	}
}

function paintCell(x, y) {
	if (currentLevelIndex < 0) return;
	const level = courseData.levels[currentLevelIndex];

	if (editMode === 'terrain' && isV2()) {
		// Every cell is ground, so there is nowhere painting is a no-op.
		const elevation = levelElevation(level);
		const rowChars = elevation[y].split('');
		rowChars[x] = String(clampHeight(selectedHeight));
		elevation[y] = rowChars.join('');
	} else {
		// Strings are immutable in JS, convert row to array, modify, join back
		let rowChars = level.grid[y].split('');
		rowChars[x] = TILES[selectedTileType].mapChar;
		level.grid[y] = rowChars.join('');
		prunePins(level);
	}

	renderGrid(); // Fast enough for small grids
	renderValidation();
	updateCourseDataOutput();
	// Note: saveState() is handled on mouseup to avoid thrashing localStorage on drag
}

/**
 * Add or remove one of this hole's alternate cups.
 *
 * The cup's own cell is refused rather than toggled: it is already where the flag stands by default, so
 * "adding" it would do nothing and removing it would suggest the hole could have no cup at all. Painting
 * over the `0` is how you move the default one.
 */
function togglePinAt(x, y) {
	if (currentLevelIndex < 0) return;
	const level = courseData.levels[currentLevelIndex];

	if (!isV2()) {
		alert('Alternate pins are a v2 feature. Switch the course format to v2 first.');
		return;
	}
	if ((level.grid[y] || '').charAt(x) === '0') {
		alert('That is the hole\u2019s own cup. Alternate pins are the other places it may be cut to.');
		return;
	}

	const pins = togglePin(level.pinPositions, x, y);
	if (pins.length > 0) level.pinPositions = pins;
	else delete level.pinPositions;

	renderGrid();
	renderValidation();
	updateCourseDataOutput();
	saveState();
}

/** Every alternate this hole currently declares, shaped against its grid. */
function levelPins(level) {
	return normalisePins(level && level.pinPositions, (level && level.grid) || []);
}

/**
 * Drop any alternate the grid has just made meaningless, after a tile write.
 *
 * The one case that actually happens: an author moves the cup onto a cell that was an alternate. It is
 * now *the* cup, and leaving it in the list would draw a flag on it that the file will not contain — or
 * worse, bring it back as an alternate the moment the cup moves on again.
 */
function prunePins(level) {
	if (!level || !level.pinPositions) return;
	const pins = levelPins(level);
	if (pins.length > 0) level.pinPositions = pins;
	else delete level.pinPositions;
}

/**
 * Every cell 4-connected to `(startX, startY)` that matches it, in tiles mode by tile character and in
 * terrain mode by elevation step — the classic paint-bucket fill. Iterative rather than recursive so a
 * fill that reaches every cell of a large hole cannot blow the call stack.
 */
function floodFill(x, y) {
	if (currentLevelIndex < 0) return;
	const level = courseData.levels[currentLevelIndex];
	const rows = level.grid.length;
	const columns = gridWidth(level.grid);

	if (editMode === 'terrain' && isV2()) {
		const elevation = levelElevation(level);
		const target = stepAt(elevation, x, y);
		if (target === clampHeight(selectedHeight)) return;
		floodApply(rows, columns, x, y, (cx, cy) => stepAt(elevation, cx, cy) === target, (cx, cy) => {
			const row = elevation[cy].split('');
			row[cx] = String(clampHeight(selectedHeight));
			elevation[cy] = row.join('');
		});
	} else {
		const targetChar = level.grid[y].charAt(x);
		const newChar = TILES[selectedTileType].mapChar;
		if (targetChar === newChar) return;
		floodApply(rows, columns, x, y, (cx, cy) => level.grid[cy].charAt(cx) === targetChar, (cx, cy) => {
			const row = level.grid[cy].split('');
			row[cx] = newChar;
			level.grid[cy] = row.join('');
		});
		prunePins(level);
	}

	renderGrid();
	renderValidation();
	saveState();
}

function floodApply(rows, columns, startX, startY, matches, apply) {
	const visited = new Set();
	const stack = [[startX, startY]];
	while (stack.length > 0) {
		const [cx, cy] = stack.pop();
		if (cx < 0 || cx >= columns || cy < 0 || cy >= rows) continue;
		const key = cy * columns + cx;
		if (visited.has(key)) continue;
		visited.add(key);
		if (!matches(cx, cy)) continue;
		apply(cx, cy);
		stack.push([cx + 1, cy], [cx - 1, cy], [cx, cy + 1], [cx, cy - 1]);
	}
}

/** How strongly the `hill` tool's falloff applies at `dist` from its centre, within `radius`: 1 at the
 *  centre easing smoothly to 0 at the edge, so the result blends into the surrounding terrain rather than
 *  stopping at a hard edge. */
function hillFalloff(dist, radius) {
	const t = Math.min(1, dist / radius);
	return (Math.cos(t * Math.PI) + 1) / 2;
}

/**
 * Sculpts a smooth rise or dip: every cell within `radius` of `anchor` blends towards `selectedHeight`,
 * strongest at the centre and fading to no change at all at the edge. Dragging further out (a bigger
 * `radius`) spreads the same change over more ground rather than making it more extreme, which is what
 * makes this a hill/dig tool rather than just a big brush.
 */
function commitHillDig(anchor, current) {
	if (currentLevelIndex < 0 || !(editMode === 'terrain' && isV2())) return;
	const level = courseData.levels[currentLevelIndex];
	const radius = Math.max(1, Math.hypot(current.x - anchor.x, current.y - anchor.y));
	const elevation = levelElevation(level);
	const rows = elevation.length;

	const minY = Math.max(0, Math.floor(anchor.y - radius));
	const maxY = Math.min(rows - 1, Math.ceil(anchor.y + radius));
	const minX = Math.max(0, Math.floor(anchor.x - radius));
	const maxX = Math.min(gridWidth(level.grid) - 1, Math.ceil(anchor.x + radius));

	for (let y = minY; y <= maxY; y++) {
		const row = elevation[y].split('');
		for (let x = minX; x <= maxX; x++) {
			const dist = Math.hypot(x - anchor.x, y - anchor.y);
			if (dist > radius) continue;
			const falloff = hillFalloff(dist, radius);
			const original = parseInt(row[x], 10);
			const blended = original + (selectedHeight - original) * falloff;
			row[x] = String(clampHeight(Math.round(blended)));
		}
		elevation[y] = row.join('');
	}

	renderGrid();
	renderValidation();
}

function setTool(tool) {
	if (tool === 'hill' && !(editMode === 'terrain' && isV2())) return;
	if (tool === 'pin' && !isV2()) {
		alert('Alternate pins are a v2 feature. Switch the course format to v2 first.');
		return;
	}
	currentTool = tool;
	dragAnchor = null;
	dragCurrent = null;
	renderToolbar();
	renderGrid();
}

function renderToolbar() {
	const terrain = editMode === 'terrain' && isV2();
	document.getElementById('toolHill').style.display = terrain ? 'inline-block' : 'none';
	if (!terrain && currentTool === 'hill') currentTool = 'brush';

	// A v1 course cannot carry alternates at all, so the tool is not offered rather than offered and
	// refused — the same treatment Hill/Dig gets outside terrain mode.
	document.getElementById('toolPin').style.display = isV2() ? 'inline-block' : 'none';
	if (!isV2() && currentTool === 'pin') currentTool = 'brush';

	const toolButtons = {
		toolBrush: 'brush',
		toolSelect: 'select',
		toolBucket: 'bucket',
		toolHill: 'hill',
		toolPin: 'pin',
	};
	Object.entries(toolButtons).forEach(([id, tool]) => {
		document.getElementById(id).classList.toggle('active', tool === currentTool);
	});
}

function normaliseRect(a, b) {
	return {
		x0: Math.min(a.x, b.x),
		x1: Math.max(a.x, b.x),
		y0: Math.min(a.y, b.y),
		y1: Math.max(a.y, b.y),
	};
}

function selectionCellCount() {
	if (!selection) return 0;
	return (selection.x1 - selection.x0 + 1) * (selection.y1 - selection.y0 + 1);
}

function clearSelection() {
	selection = null;
	renderGrid();
}

function updateSelectionControls() {
	const clearBtn = document.getElementById('clearSelectionBtn');
	if (clearBtn) clearBtn.style.display = selection ? 'inline-block' : 'none';

	const fillBtn = document.getElementById('fillBtn');
	if (!fillBtn) return;
	const terrain = editMode === 'terrain' && isV2();
	if (selection) {
		fillBtn.textContent = terrain
			? `Fill Selected Area (${selectionCellCount()} cells) with Height`
			: `Fill Selected Area (${selectionCellCount()} cells) with Tile`;
	} else {
		fillBtn.textContent = terrain ? 'Fill Hole with Height' : 'Fill with Selected Tile';
	}
}

// --- v2: format editing ---

function changeCourseFormat() {
	const wanted = parseInt(document.getElementById('courseFormat').value, 10) === 1 ? 1 : 2;
	if (wanted === courseFormat()) return;

	if (wanted === 1) {
		const losing = [];
		if (courseData.wind) losing.push('the wind');
		// The editor no longer authors a green contour, but a course loaded from before that change may
		// still carry one, and downgrading it to v1 would silently throw that work away.
		if (courseData.levels.some((level) => hasContour(level.grid || [], level.heights || []))) {
			losing.push('every green contour');
		}
		if (courseData.levels.some((level) => hasElevation(level.elevation || []))) {
			losing.push('every hole\'s terrain');
		}
		if (courseData.levels.some((level) => levelPins(level).length > 0)) {
			losing.push('every alternate pin');
		}
		// Width is not in that list because it is not thrown away — the grids keep their columns. It is
		// played differently, which is worse to find out afterwards: the game reads a v1 hole at exactly
		// `DEFAULT_WIDTH`, so a wider hole loses its right-hand columns and a narrower one gains
		// out-of-bounds ones. Said as its own sentence rather than folded in with the deletions.
		const rewidening = courseData.levels.some((level) => gridWidth(level.grid || []) !== DEFAULT_WIDTH);
		const them = losing.length > 1 ? 'them' : 'it';
		const sentences = [];
		if (losing.length > 0) {
			sentences.push(`Switching to v1 removes ${losing.join(' and ')}. A v1 course cannot carry ${them}.`);
		}
		if (rewidening) {
			sentences.push(
				`v1 plays every hole at exactly ${DEFAULT_WIDTH} columns, so holes of any other width will be cropped or padded.`);
		}
		if (sentences.length > 0 && !confirm(`${sentences.join(' ')} Continue?`)) {
			// Put the control back where it was; the state never moved.
			document.getElementById('courseFormat').value = String(courseFormat());
			return;
		}
		delete courseData.wind;
		courseData.levels.forEach((level) => {
			delete level.heights;
			delete level.elevation;
			delete level.pinPositions;
		});
		courseData.formatVersion = 1;
		editMode = 'tiles';
	} else {
		courseData.formatVersion = 2;
	}

	renderAll();
	saveState();
}

function setEditMode(mode) {
	if (mode === 'terrain' && !isV2()) {
		alert('Terrain elevation is a v2 feature. Switch the course format to v2 first.');
		return;
	}
	editMode = mode === 'terrain' ? 'terrain' : 'tiles';
	renderPaletteMode();
	renderGrid();
}

function renderPaletteMode() {
	const terrain = editMode === 'terrain' && isV2();

	document.getElementById('tileSelector').style.display = terrain ? 'none' : 'flex';
	document.getElementById('terrainPanel').style.display = terrain ? 'flex' : 'none';
	document.getElementById('terrainHelp').style.display = terrain ? 'block' : 'none';
	document.getElementById('paletteTitle').textContent = terrain ? 'Ground Height' : 'Tile Palette';

	document.getElementById('modeTiles').classList.toggle('active', !terrain);
	document.getElementById('modeTerrain').classList.toggle('active', terrain);

	renderToolbar();
}

function initHeightSelector() {
	const selector = document.getElementById('heightSelector');
	if (!selector) return;
	selector.innerHTML = '';

	for (let height = MIN_HEIGHT; height <= MAX_HEIGHT; height++) {
		const button = document.createElement('div');
		button.className = 'height-button';
		button.textContent = String(height);
		// The same low-to-high ramp the grid shades with, so the palette reads as an elevation scale
		// rather than as ten arbitrary buttons.
		const level = height / MAX_HEIGHT;
		const shade = Math.round(40 + level * 180);
		button.style.backgroundColor = `rgb(${shade}, ${Math.round(shade * 0.85 + 30)}, ${shade})`;
		button.onclick = () => selectHeight(height);
		button.dataset.height = String(height);
		selector.appendChild(button);
	}
	selectHeight(selectedHeight);
}

function selectHeight(height) {
	selectedHeight = clampHeight(height);
	document.querySelectorAll('.height-button').forEach((button) => {
		button.classList.toggle('selected', parseInt(button.dataset.height, 10) === selectedHeight);
	});
}

function clearTerrain() {
	if (currentLevelIndex < 0) return;
	const level = courseData.levels[currentLevelIndex];
	if (!confirm('Flatten this hole? Its terrain will be removed from the file.')) return;

	delete level.elevation;
	renderGrid();
	renderValidation();
	saveState();
}

/** Whether the Course Problems panel is collapsed. Not persisted — a course with problems should
 *  open expanded on reload rather than hide them because of a stale choice. */
let validationCollapsed = false;

function toggleValidation() {
	validationCollapsed = !validationCollapsed;
	document.getElementById('validationPanel')?.classList.toggle('collapsed', validationCollapsed);
}

function renderValidation() {
	const panel = document.getElementById('validationPanel');
	const title = document.getElementById('validationTitle');
	const body = document.getElementById('validationBody');
	if (!panel || !title || !body) return;

	panel.classList.toggle('collapsed', validationCollapsed);

	const warnings = courseWarnings(courseData);
	if (warnings.length === 0) {
		panel.classList.remove('has-problems');
		panel.classList.add('ok');
		title.textContent = `✓ Course Problems — ${courseData.levels.length} hole(s), no problems found.`;
		body.innerHTML = '';
		return;
	}

	panel.classList.remove('ok');
	panel.classList.add('has-problems');
	title.textContent = `Course Problems (${warnings.length})`;
	body.innerHTML = '<ul>' + warnings.map((warning) => `<li>${warning}</li>`).join('') + '</ul>';
}

/**
 * Resize the hole.
 *
 * Height has always been per-hole; width is new, and the two are asked for together because they are one
 * question — "how big is this hole" — and answering it in two dialogs invites an author to change one and
 * forget the other.
 *
 * Width is v2-only, for the same reason the terrain and the pins are: the game plays *every* v1 hole at
 * exactly `DEFAULT_WIDTH`, padding a short row and discarding anything past it, so a 30-wide v1 hole
 * would be a hole the editor draws one way and the game plays another. A v1 course is told so and keeps
 * its height dialog.
 *
 * Growing is free. Shrinking throws away whatever was painted in the columns and rows that go, which is
 * why it asks first — and why the caller of `resizedGrid` is the one holding the confirmation, not
 * `resizedGrid` itself.
 */
function changeGridSize() {
	if (currentLevelIndex < 0) return;
	const level = courseData.levels[currentLevelIndex];
	const currentWidth = gridWidth(level.grid);
	const currentHeight = level.grid.length;

	let newWidth = currentWidth;
	if (isV2()) {
		const answer = prompt(
			`Width in tiles (${WIDTH_RANGE.min}\u2013${WIDTH_RANGE.max}):`, currentWidth);
		if (answer === null) return;
		if (answer.trim() === '' || isNaN(answer)) return;
		newWidth = clampToRange(parseInt(answer, 10), WIDTH_RANGE);
	}

	const answer = prompt(
		`Height in rows (${HEIGHT_RANGE.min}\u2013${HEIGHT_RANGE.max}):`, currentHeight);
	if (answer === null) return;
	if (answer.trim() === '' || isNaN(answer)) return;
	const newHeight = clampToRange(parseInt(answer, 10), HEIGHT_RANGE);

	if (newWidth === currentWidth && newHeight === currentHeight) return;

	// Named so the author can see which way each axis went before agreeing to lose anything.
	const losing = [];
	if (newWidth < currentWidth) losing.push(`${currentWidth - newWidth} column(s) from the right`);
	if (newHeight < currentHeight) losing.push(`${currentHeight - newHeight} row(s) from the bottom`);
	if (losing.length > 0 && !confirm(`This crops ${losing.join(' and ')}. Continue?`)) return;

	level.grid = resizedGrid(level.grid, newWidth, newHeight);
	// Both matrices are parallel to the grid, so they have to follow it in both directions — a matrix a
	// column short would read as a step change down the new edge rather than as ground continuing.
	// Only a legacy course still carries `heights`; the editor no longer authors one.
	if (level.heights) level.heights = normaliseHeights(level.heights, level.grid);
	if (level.elevation) level.elevation = normaliseElevation(level.elevation, level.grid);
	// A pin outside the new bounds is a flag off the end of the hole. Dropped rather than clamped: there
	// is no cell it obviously belongs on instead, and a cup silently relocated is worse than one the
	// author has to place again.
	if (level.pinPositions) {
		const pins = normalisePins(level.pinPositions, level.grid);
		if (pins.length > 0) level.pinPositions = pins;
		else delete level.pinPositions;
	}
	// A selection made against the old size may no longer fit the new one.
	clearSelection();
	renderGrid();
	renderValidation();
	updateCourseDataOutput();
	saveState();
}

/**
 * Fills either the whole grid, or just the selected rectangle when `select` has one active — the same
 * button does both, since "fill" means the same thing either way and a selection is just a smaller target.
 */
function fillGrid() {
	if (currentLevelIndex < 0) return;
	const level = courseData.levels[currentLevelIndex];
	const areaLabel = selection ? `the selected area (${selectionCellCount()} cells)` : 'the entire grid';

	if (editMode === 'terrain' && isV2()) {
		if (!confirm(`Set ${areaLabel} to height ${selectedHeight}?`)) return;
		const elevation = levelElevation(level);
		forEachAreaCell(level, (x, y) => {
			const row = elevation[y].split('');
			row[x] = String(clampHeight(selectedHeight));
			elevation[y] = row.join('');
		});
		renderGrid();
		renderValidation();
		saveState();
		return;
	}

	if (!confirm(`Fill ${areaLabel} with current tile?`)) return;

	const char = TILES[selectedTileType].mapChar;
	forEachAreaCell(level, (x, y) => {
		const row = level.grid[y].split('');
		row[x] = char;
		level.grid[y] = row.join('');
	});
	prunePins(level);

	renderGrid();
	renderValidation();
	saveState();
}

/** Every `(x, y)` the fill/paint tools should act on: the active selection, or the whole grid. */
function forEachAreaCell(level, fn) {
	if (selection) {
		for (let y = selection.y0; y <= selection.y1; y++) {
			for (let x = selection.x0; x <= selection.x1; x++) fn(x, y);
		}
		return;
	}
	const columns = gridWidth(level.grid);
	for (let y = 0; y < level.grid.length; y++) {
		for (let x = 0; x < columns; x++) fn(x, y);
	}
}

function getExportCourseData() {
	return exportCourse(courseData);
}

// --- Import / Export ---

async function saveToFile() {
	const courseId = courseData.id || 'new_course';
	const dataStr = JSON.stringify(getExportCourseData(), null, 4);

	try {
		// Use File System Access API if supported, prompts user for path and filename
		if (window.showSaveFilePicker) {
			const handle = await window.showSaveFilePicker({
				suggestedName: courseId + '.pocketgolf',
				types: [
					{
						description: 'Pocket Golf Course',
						accept: { 'application/json': ['.pocketgolf'] },
					},
				],
			});
			const writable = await handle.createWritable();
			await writable.write(dataStr);
			await writable.close();
			showSaveStatus('Saved to chosen file successfully');
		} else {
			// Fallback to classic download
			const blob = new Blob([dataStr], { type: 'text/json;charset=utf-8' });
			const url = URL.createObjectURL(blob);
			const downloadAnchorNode = document.createElement('a');
			downloadAnchorNode.setAttribute('href', url);
			downloadAnchorNode.setAttribute('download', courseId + '.pocketgolf');
			document.body.appendChild(downloadAnchorNode);
			downloadAnchorNode.click();
			downloadAnchorNode.remove();
			URL.revokeObjectURL(url);
			showSaveStatus('Downloaded to file');
		}
	} catch (err) {
		if (err.name !== 'AbortError') {
			alert('Save File failed: ' + err.message);
		}
	}
}

function copyJsonToClipboard() {
	const dataStr = JSON.stringify(getExportCourseData(), null, 4);
	navigator.clipboard
		.writeText(dataStr)
		.then(() => {
			showSaveStatus('JSON copied to clipboard!');
		})
		.catch((err) => {
			alert('Failed to copy text: ' + err);
		});
}

/**
 * Show or hide the course properties column.
 *
 * A hole can be 50 tiles wide now, which is 1500px of grid on a column that used to have 450px of
 * form beside it permanently. Collapsing gives that back without touching the zoom: the cells stay
 * 30px, because shrinking them is what makes a grid unreadable.
 *
 * The state is a view preference, so it is kept apart from `pocketGolfCourse` — a course is a file an
 * author exports and shares, and whether their sidebar was open is nobody else's business.
 */
function togglePropertiesPanel(collapsed) {
	const layout = document.getElementById('editorLayout');
	if (!layout) return;

	// An explicit argument is the restore, which has to land instantly; no argument is a click, which
	// is the only thing worth animating.
	if (collapsed === undefined) layout.classList.add(PROPERTIES_ANIMATED);
	const next = collapsed === undefined ? !layout.classList.contains(PROPERTIES_COLLAPSED) : collapsed;
	layout.classList.toggle(PROPERTIES_COLLAPSED, next);

	const toggle = document.getElementById('propertiesToggle');
	if (toggle) {
		toggle.setAttribute('aria-expanded', String(!next));
		toggle.title = next ? 'Show the course properties' : 'Hide the course properties';
	}
	const chevron = document.getElementById('propertiesToggleChevron');
	if (chevron) chevron.innerHTML = next ? '&raquo;' : '&laquo;';

	try {
		localStorage.setItem(PROPERTIES_STORAGE_KEY, next ? '1' : '0');
	} catch (e) {
		// A browser with storage blocked still gets a working toggle; it just forgets between visits.
	}
}

/** The collapse state from last time, applied before the first paint of the layout. */
function restorePropertiesPanel() {
	let stored = null;
	try {
		stored = localStorage.getItem(PROPERTIES_STORAGE_KEY);
	} catch (e) {
		stored = null;
	}
	togglePropertiesPanel(stored === '1');
}

function copyHoleGrid() {
	if (currentLevelIndex < 0) return;
	const level = courseData.levels[currentLevelIndex];
	const gridStr = JSON.stringify(level.grid, null, 4);
	navigator.clipboard
		.writeText(gridStr)
		.then(() => {
			showSaveStatus('Hole grid copied to clipboard!');
		})
		.catch((err) => {
			alert('Failed to copy text: ' + err);
		});
}

function loadFromFile(event) {
	const file = event.target.files[0];
	if (!file) {
		return;
	}
	loadCourseFile(file);
	// Reset the input value so the same file could be loaded again
	event.target.value = '';
}

/** Shared by the file picker and drag-and-drop: both just hand over a File. */
function loadCourseFile(file) {
	const reader = new FileReader();
	reader.onload = function (e) {
		try {
			const contents = e.target.result;
			courseData = importCourse(JSON.parse(contents));

			editMode = 'tiles';
			currentTool = 'brush';
			selection = null;
			currentLevelIndex = courseData.levels.length > 0 ? 0 : -1;
			updateAllLevelIds(); // Keep ID consistency even on load
			renderAll();
			saveState();
			showSaveStatus('Loaded from file successfully!');
		} catch (err) {
			alert('Error parsing JSON from file: ' + err.message);
		}
	};
	reader.readAsText(file);
}

// --- Drag and drop ---

/**
 * The browser's default action for a dropped file is to navigate to it, which would blow away the
 * editor's state. Every drag event has to be intercepted at the window level (not just the drop
 * zone) so a mis-aimed drop never reaches that default.
 */
function setupDragAndDrop() {
	['dragenter', 'dragover'].forEach((eventName) => {
		window.addEventListener(eventName, (e) => {
			e.preventDefault();
			if (e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files')) {
				document.body.classList.add('drag-over');
			}
		});
	});

	window.addEventListener('dragleave', (e) => {
		e.preventDefault();
		// relatedTarget is null once the pointer leaves the window itself, not just a child element -
		// without this check the overlay flickers off while dragging over child nodes.
		if (!e.relatedTarget) {
			document.body.classList.remove('drag-over');
		}
	});

	window.addEventListener('drop', (e) => {
		e.preventDefault();
		document.body.classList.remove('drag-over');
		const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
		if (!file) return;
		if (!/\.(pocketgolf|json)$/i.test(file.name)) {
			alert('Please drop a .pocketgolf or .json course file.');
			return;
		}
		loadCourseFile(file);
	});
}

function clearEntireCourse() {
	if (confirm('WARNING: This will delete ALL levels and course data and prepopulate 9 empty holes. Are you sure?')) {
		createDefaultCourse();
		selection = null;
		renderAll();
		saveState();
	}
}

// Initialize on load
// `type=module` defers this to after the document is parsed and before it is painted, which is where a
// remembered layout has to be applied: `onload` is late enough to show the panel open first.
restorePropertiesPanel();

window.onload = init;

// Expose functions to global scope for HTML event handlers
window.updateCourseData = updateCourseData;
window.loadFromFile = loadFromFile;
window.saveToFile = saveToFile;
window.copyJsonToClipboard = copyJsonToClipboard;
window.clearEntireCourse = clearEntireCourse;
window.toggleValidation = toggleValidation;
window.updateLevelData = updateLevelData;
window.addLevel = addLevel;
window.deleteLevel = deleteLevel;
window.copyHoleGrid = copyHoleGrid;
window.togglePropertiesPanel = togglePropertiesPanel;
window.changeGridSize = changeGridSize;
window.fillGrid = fillGrid;
window.changeCourseFormat = changeCourseFormat;
window.setEditMode = setEditMode;
window.setTool = setTool;
window.clearSelection = clearSelection;
window.clearTerrain = clearTerrain;
